import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import PortalLayout from "../components/PortalLayout";
import { useAuth } from "../context/AuthContext";
import { parseErrorDetail } from "../lib/api";

// R6 password policy — same rules the server enforces.
const RULES = [
  { key: "length",  label: "At least 12 characters",       test: (p) => p.length >= 12 },
  { key: "upper",   label: "One upper-case letter (A–Z)",  test: (p) => /[A-Z]/.test(p) },
  { key: "lower",   label: "One lower-case letter (a–z)",  test: (p) => /[a-z]/.test(p) },
  { key: "digit",   label: "One digit (0–9)",              test: (p) => /[0-9]/.test(p) },
  { key: "symbol",  label: "One symbol (non-letter, non-digit)", test: (p) => /[^A-Za-z0-9\s]/.test(p) },
];

export default function SettingsSecurity() {
  const { user, changePassword } = useAuth();
  const nav = useNavigate();
  const [oldPw, setOldPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const ruleStatus = useMemo(
    () => RULES.map((r) => ({ ...r, ok: newPw ? r.test(newPw) : false })),
    [newPw],
  );
  const allPolicyOk = ruleStatus.every((r) => r.ok);
  const confirmOk = confirmPw && confirmPw === newPw;
  const differsFromCurrent = !!oldPw && !!newPw && oldPw !== newPw;
  const canSubmit = !submitting && !!oldPw && allPolicyOk && confirmOk && differsFromCurrent;

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!allPolicyOk) { setError("New password does not meet the policy."); return; }
    if (!confirmOk)   { setError("New passwords do not match."); return; }
    if (!differsFromCurrent) { setError("New password must differ from your current password."); return; }
    setSubmitting(true);
    try {
      const res = await changePassword(oldPw, newPw, confirmPw);
      if (res?.must_relogin) {
        nav("/login", { replace: true, state: { pwChanged: true } });
        return;
      }
      setError("Password changed, but you were not signed out. Please sign in again.");
    } catch (err) {
      setError(parseErrorDetail(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <PortalLayout title="Settings → Security">
      <section className="apwo-settings-shell" data-testid="settings-security-shell">
        <div className="apwo-settings-card" data-testid="settings-security-card">
          <header className="apwo-settings-header">
            <h2 className="apwo-settings-heading" data-testid="settings-security-heading">Change password</h2>
            <p className="apwo-settings-sub">
              Signed in as <strong>{user?.display_name || user?.username}</strong>.
              Once you set a new password, every active session on your account
              (including this browser) will be signed out. You&apos;ll need to sign in
              again with the new password.
            </p>
          </header>

          <form className="apwo-auth-form" onSubmit={onSubmit} noValidate data-testid="settings-security-form">
            <label className="apwo-field">
              <span>Current password</span>
              <input
                type="password"
                autoComplete="current-password"
                value={oldPw}
                onChange={(e) => setOldPw(e.target.value)}
                required
                data-testid="settings-pw-current"
              />
            </label>

            <label className="apwo-field">
              <span>New password</span>
              <input
                type="password"
                autoComplete="new-password"
                value={newPw}
                onChange={(e) => setNewPw(e.target.value)}
                required
                minLength={12}
                data-testid="settings-pw-new"
              />
            </label>

            <ul className="apwo-policy-list" data-testid="settings-pw-policy">
              {ruleStatus.map((r) => (
                <li
                  key={r.key}
                  className={`apwo-policy-item apwo-policy-item--${r.ok ? "ok" : "todo"}`}
                  data-testid={`settings-pw-rule-${r.key}`}
                  data-ok={r.ok ? "true" : "false"}
                >
                  <span className="apwo-policy-mark" aria-hidden="true">{r.ok ? "✓" : "•"}</span>
                  <span>{r.label}</span>
                </li>
              ))}
              <li
                className={`apwo-policy-item apwo-policy-item--${differsFromCurrent ? "ok" : "todo"}`}
                data-testid="settings-pw-rule-differs"
                data-ok={differsFromCurrent ? "true" : "false"}
              >
                <span className="apwo-policy-mark" aria-hidden="true">{differsFromCurrent ? "✓" : "•"}</span>
                <span>Different from your current password</span>
              </li>
            </ul>

            <label className="apwo-field">
              <span>Confirm new password</span>
              <input
                type="password"
                autoComplete="new-password"
                value={confirmPw}
                onChange={(e) => setConfirmPw(e.target.value)}
                required
                minLength={12}
                data-testid="settings-pw-confirm"
              />
            </label>

            {error && (
              <div className="apwo-alert-error" role="alert" data-testid="settings-pw-error">
                {error}
              </div>
            )}

            <button
              type="submit"
              className="apwo-btn-primary"
              disabled={!canSubmit}
              data-testid="settings-pw-submit"
            >
              {submitting ? "Saving…" : "Change password"}
            </button>
          </form>
        </div>
      </section>
    </PortalLayout>
  );
}
